import React, { useState, useEffect } from "react";
import {
  FiFilter,
  FiCheck,
  FiX,
  FiClock,
  FiUser,
  FiPhone,
  FiMail,
  FiDollarSign,
  FiCalendar,
  FiImage,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { DositPending, ApprovePending, RejectPending } from "../../api/endpoints";
import Swal from "../../utils/swalCompat";

import PageHeader from "../../components/PageHeader";

const DepositPending = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  console.log("Selected Image URL:", selectedImage); // Debugging line
  const [baseUrl, setBaseUrl] = useState("");

  // ✅ Fetch Pending Deposits
  const fetchPendingDeposits = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await DositPending(params);
      console.log("Pending Deposits:", res);

      // ✅ Save baseUrl from response
      if (res.data?.baseUrl) {
        setBaseUrl(res.data.baseUrl);
      }

      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching pending deposits:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch pending deposits",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingDeposits();
  }, [page, search, startDate, endDate]);

  // ✅ Handle Approve
  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Approve Deposit?",
      text: "Are you sure you want to approve this deposit?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setActionLoading(id);
        const res = await ApprovePending({ id });
        console.log("Approve Response:", res);

        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: "Deposit approved successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchPendingDeposits();
      } catch (error) {
        console.error("Error approving deposit:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to approve deposit",
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  // ✅ Handle Reject
  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: "Reject Deposit?",
      text: "Are you sure you want to reject this deposit?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setActionLoading(id);
        const res = await RejectPending({ id });
        console.log("Reject Response:", res);

        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: "Deposit rejected successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchPendingDeposits();
      } catch (error) {
        console.error("Error rejecting deposit:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to reject deposit",
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Get Full Image URL
  const getFullImageUrl = (screenshot) => {
    if (!screenshot) return "";

    return screenshot.startsWith("http")
      ? screenshot
      : `${baseUrl}/${screenshot}`;
  };

  // ✅ Get Status Badge
  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { color: "badge-light-warning", icon: <FiClock />, label: "Pending" },
      APPROVED: { color: "badge-light-success", icon: <FiCheck />, label: "Approved" },
      REJECTED: { color: "badge-light-danger", icon: <FiX />, label: "Rejected" },
    };
    const statusInfo = statusMap[status] || statusMap.PENDING;
    return (
      <span className={`${statusInfo.color}`}>
        {statusInfo.icon} {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="lead-page">
      <div className="deposit-pending-page">
        {/* Header */}
        <PageHeader
          eyebrow="Manual Payment"
          title="Deposit Pending"
          subtitle="Manage and approve pending deposit requests."
          icon={<FiClock />}
          tone="warning"
          stat={{ label: "Total Pending", value: total }}
        />

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Card Header */}
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
            <h5 className="mb-0 fw-bold">
              <FiClock className="me-2" />
              Pending Deposit Requests
            </h5>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {showFilter && (
                <>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by User, UTR..."
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    style={{ width: "200px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setPage(1);
                      setStartDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setPage(1);
                      setEndDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                </>
              )}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter /> Filter
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchPendingDeposits}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw /> Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th style={{ minWidth: "200px" }}>User Details</th>
                    <th style={{ width: "120px" }}>Amount</th>
                    <th style={{ minWidth: "130px" }}>UTR Number</th>
                    <th style={{ width: "120px" }}>Payment</th>
                    <th style={{ width: "80px" }}>Screenshot</th>
                    <th style={{ minWidth: "150px" }}>Date</th>
                    <th style={{ width: "110px" }}>Status</th>
                    <th style={{ width: "200px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading deposits...</div>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => {
                      const imageUrl = getFullImageUrl(item.screenshot);
                      console.log("dasdasdasdasdasd", getFullImageUrl(item.screenshot));
                      return (
                        <tr key={item._id}>
                          <td className="fw-bold">{(page - 1) * limit + index + 1}</td>

                          {/* User Details */}
                          <td>
                            <div className="d-flex flex-column">
                              <div className="d-flex align-items-center gap-1">
                                <FiUser size={12} className="text-primary" />
                                <strong>{item.userName || "N/A"}</strong>
                              </div>
                              <div className="text-muted small">
                                <FiPhone size={10} /> {item.userMobile || "N/A"}
                              </div>
                              {/* <div className="text-muted small">
                                <FiMail size={10} /> {item.userEmail || "N/A"}
                              </div>
                              <div className="text-muted small">
                                <span className="badge bg-light text-dark">
                                  Referral: {item.userReferral || "-"}
                                </span>
                              </div> */}
                            </div>
                          </td>

                          {/* Amount */}
                          <td>
                            <span className="fw-bold text-success fs-5">
                              ₹ {item.amount?.toLocaleString() || 0}
                            </span>
                          </td>

                          {/* UTR Number */}
                          <td>
                            <span className="badge_utr">
                              {item.utrNumber || "N/A"}
                            </span>
                          </td>

                          {/* Payment Method */}
                          <td>
                            <span className="badge bg-secondary bg-opacity-10 text-dark">
                              {item.paymentMethod || "N/A"}
                            </span>
                          </td>

                          {/* Screenshot */}
                          <td>
                            {imageUrl ? (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => setSelectedImage(getFullImageUrl(item.screenshot))}
                              >
                                <FiEye /> View
                              </button>
                            ) : (
                              <span className="text-muted">N/A</span>
                            )}
                          </td>

                          {/* Date */}
                          <td>
                            <div className="small">
                              <FiCalendar size={10} className="me-1" />
                              {formatDate(item.createdAt)}
                            </div>
                          </td>

                          {/* Status */}
                          <td>{getStatusBadge(item.status)}</td>

                          {/* Action */}
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-success btn-sm px-3"
                                onClick={() => handleApprove(item._id)}
                                disabled={actionLoading === item._id}
                              >
                                {actionLoading === item._id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <>
                                    <FiCheck /> Approve
                                  </>
                                )}
                              </button>
                              <button
                                className="btn btn-danger btn-sm px-3"
                                onClick={() => handleReject(item._id)}
                                disabled={actionLoading === item._id}
                              >
                                {actionLoading === item._id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <>
                                    <FiX /> Reject
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="text-muted">
                          <FiClock size={40} className="mb-3 d-block mx-auto text-secondary" />
                          <h5 className="fw-bold">No Pending Deposits</h5>
                          <p className="small">All deposit requests have been processed</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} entries
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <span className="fw-bold px-3">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Screenshot Modal - Fixed */}
        {selectedImage && (
          <div
            className="modal show d-block"
            style={{
              backgroundColor: "rgba(0,0,0,0.7)",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
            onClick={() => setSelectedImage(null)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: "600px", width: "100%", margin: "auto" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Deposit Screenshot</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedImage(null)}
                  ></button>
                </div>
                <div className="modal-body text-center">
                  <img
                    src={selectedImage}
                    alt="Deposit Screenshot"
                    crossOrigin="anonymous"
                    className="img-fluid rounded"
                    style={{ maxHeight: "400px", objectFit: "contain" }}
                    onLoad={() => console.log("Image Loaded")}
                    onError={(e) => {
                      console.log("Image Error");
                      console.log(e.target.src);
                    }}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedImage(null)}
                  >
                    Close
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.open(selectedImage, "_blank")}
                  >
                    Open Full Size
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepositPending;
